import { JsonApiRequestError, Mutation } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { ComboboxItem } from '@mantine/core';
import { sample, Store } from 'effector';
import { or } from 'patronum';
import { accountTreeModel } from 'entities/account';
import {
	AddJackpotMutationParams,
	AddJackpotResponse,
	EntityWithName,
	JackpotType,
	createAddJackpotMutation,
	createGetAccountQuery,
	createGetAccountsQuery,
	createGetGamesQuery,
} from 'shared/api';
import {
	createModalFormModel,
	greaterZeroRule,
	ModalFormModel,
	notEmptyRule,
	notify,
} from 'shared/lib';

export type AddJackpotFormValue = {
	accountId: number | null;
	gameId: number | null;
	currency: string | null;
	type: JackpotType | null;
	betRate: string;
	initialAmount: string;
	limitAmount: string;
};

export type AddJackpotModel = ModalFormModel<null, AddJackpotFormValue> & {
	addJackpotMutation: Mutation<AddJackpotMutationParams, AddJackpotResponse, JsonApiRequestError>;
	$pending: Store<boolean>;
	$currencies: Store<string[]>;
	$accounts: Store<EntityWithName[]>;
	$games: Store<ComboboxItem[]>;
};

export const createAddJackpotModel = (): AddJackpotModel => {
	const addJackpotMutation = createAddJackpotMutation();

	const getAccountsQuery = createGetAccountsQuery();

	const getAccountQuery = createGetAccountQuery();

	const getGamesQuery = createGetGamesQuery();

	const $games = getGamesQuery.$data.map((games) =>
		games ? games.map((game) => ({ value: `${game.id}`, label: game.title })) : [],
	);

	const model = createModalFormModel<null, AddJackpotFormValue>({
		fields: {
			accountId: {
				init: null,
				rules: [notEmptyRule],
			},
			gameId: {
				init: null,
				rules: [notEmptyRule],
			},
			currency: {
				init: null,
				rules: [notEmptyRule],
			},
			type: {
				init: null,
				rules: [notEmptyRule],
			},
			betRate: {
				init: '',
				rules: [notEmptyRule, greaterZeroRule],
			},
			initialAmount: {
				init: '',
				rules: [notEmptyRule],
			},
			limitAmount: {
				init: '',
				rules: [notEmptyRule, greaterZeroRule],
			},
		},
		resetErrorsOnChange: true,
		validateOn: ['submit'],
	});

	const $accounts = getAccountsQuery.$data.map((result) => result ?? []);

	const $currencies = getAccountQuery.$data.map((data) =>
		data ? data.wallets.filter(({ isLocked }) => !isLocked).map((wallet) => wallet.currency) : [],
	);

	const $pending = or(
		getAccountsQuery.$pending,
		getAccountQuery.$pending,
		addJackpotMutation.$pending,
		getGamesQuery.$pending,
	);

	sample({
		clock: model.modalButtonClicked,
		target: [
			model.form.fields.accountId.disable,
			model.form.fields.gameId.disable,
			model.form.fields.currency.disable,
			getAccountsQuery.start,
			getGamesQuery.start,
		],
	});

	sample({
		clock: model.modalButtonClicked,
		source: accountTreeModel.$singleSelectedNode,
		filter: (accountId): accountId is number => accountId !== null,
		target: model.form.fields.accountId.setValue,
	});

	sample({
		clock: model.form.fields.accountId.$value,
		target: [model.form.fields.currency.disable, model.form.fields.currency.reset],
	});

	sample({
		clock: model.form.fields.accountId.$value,
		filter: Boolean,
		target: getAccountQuery.start,
	});

	chain(getAccountsQuery.finished.success, model.form.fields.accountId.enable);

	chain(getGamesQuery.finished.success, model.form.fields.gameId.enable);

	chain(getAccountQuery.finished.success, model.form.fields.currency.enable);

	sample({
		clock: model.form.submitted,
		source: model.form.$values,
		filter: ({ initialAmount, limitAmount }) => Number(limitAmount) >= Number(initialAmount),
		fn: (values) => ({
			accountId: values.accountId ?? 0,
			gameId: values.gameId ?? 0,
			currency: values.currency!,
			type: values.type!,
			betRate: Number(values.betRate) / 100,
			initialAmount: Number(values.initialAmount),
			limitAmount: Number(values.limitAmount),
		}),
		target: addJackpotMutation.start,
	});

	sample({
		clock: model.form.submitted,
		source: model.form.$values,
		filter: ({ initialAmount, limitAmount }) => Number(limitAmount) < Number(initialAmount),
		fn: () => 'one-field-greater-than-second-field',
		target: model.form.fields.limitAmount.addError,
	});

	chain(addJackpotMutation.finished.success, model.switcher.disable);

	notify(addJackpotMutation.finished.success, 'add-jackpot.success');

	sample({
		clock: model.modalClosed,
		target: [
			getAccountsQuery.reset,
			getAccountQuery.reset,
			addJackpotMutation.reset,
			getGamesQuery.reset,
		],
	});

	return {
		addJackpotMutation,
		$accounts,
		$pending,
		$currencies,
		$games,
		...model,
	};
};
